import JSZip from 'jszip';
import { Artifact } from '../types';

/** Triggers a browser download for a blob via a temporary anchor element. */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has time to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Decodes a base64 string into a Blob of raw bytes with the given MIME type. */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/** Downloads a single artifact, decoding base64 binaries back to their raw bytes. */
export function downloadArtifact(artifact: Artifact) {
  const blob =
    artifact.encoding === 'base64'
      ? base64ToBlob(artifact.content, artifact.mimeType || 'application/octet-stream')
      : new Blob([artifact.content], { type: 'text/plain;charset=utf-8' });
  triggerDownload(blob, artifact.name);
}

/** Slugifies a step label into a safe zip filename. */
function safeZipName(label: string): string {
  return (
    label
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'artifacts'
  );
}

/**
 * Downloads all artifacts for a step. A single artifact downloads as a plain
 * file; multiple artifacts are bundled into one `<step>.zip`. Duplicate
 * filenames are de-duplicated with a numeric suffix.
 */
export async function downloadArtifactsZip(label: string, artifacts: Artifact[]) {
  if (artifacts.length === 0) return;
  if (artifacts.length === 1) {
    downloadArtifact(artifacts[0]);
    return;
  }

  const zip = new JSZip();
  const seen = new Map<string, number>();
  for (const a of artifacts) {
    const count = seen.get(a.name) ?? 0;
    seen.set(a.name, count + 1);
    let name = a.name;
    if (count > 0) {
      const dot = name.lastIndexOf('.');
      name =
        dot > 0
          ? `${name.slice(0, dot)}-${count}${name.slice(dot)}`
          : `${name}-${count}`;
    }
    zip.file(name, a.content, { base64: a.encoding === 'base64' });
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  triggerDownload(blob, `${safeZipName(label)}.zip`);
}
